/**
 * Tibbs's readout, the pure half (docs/TIBBS-VOICE.md, after Single Didge's
 * public/shared/readout.js; see docs/SINGLE-DIDGE-READOUT-ANALYSIS.md). It
 * turns a finished hole into a structured "facts" object (standings, gaps,
 * places, what each player's cards did, the game so far) for the model to
 * write from. Nothing here touches the network or the DOM: it runs in the
 * Worker, the Durable Object, the browser and the tests alike.
 *
 * Flow: the client (vs computer) or the room (online) builds a ReadoutRequest
 * from the game state (`readoutRequestFromState`), the Worker validates it
 * (`validateReadoutRequest`) and computes the facts (`computeFacts`), and the
 * prompt (prompt.ts) is built from those facts.
 */
import { scoreGrid } from '../engine';
import type { GameEvent, GameState } from '../engine';
import { CARD_NAMES, WORLDS } from '../client/brand';
import { LAST_PRIZE } from './prizes';

export const MAX_NAME = 24;
export const MAX_READOUT_PLAYERS = 8;
export const MAX_TOTAL_HOLES = 18;
/** Sudden-death holes after regulation: far more than any real game plays. */
export const MAX_PLAYOFF_HOLES = 30;
/** Eight cards: the best possible hole is four Eureka columns plus the bonus; the worst is eight twelves. */
export const MIN_HOLE_SCORE = -60;
export const MAX_HOLE_SCORE = 96;
export const MAX_GAG_CHARS = 140;
export const MAX_PREVIOUS_SCRIPTS = 2;
export const MAX_PREVIOUS_CHARS = 1200;

/** Score tiers from the character bible (TIBBS-VOICE.md 1.2, 3.2 and 7). */
export const LEGENDARY_SCORE = -20;
export const HM_SCORE = 20;
export const OH_DEAR_SCORE = 30;
/** Skips on one hole that count as patience (7.14). */
export const PATIENCE_SKIPS = 3;

// ---------------------------------------------------------------------------
// The tour

export interface Course {
  n: number;
  name: string;
  place: string;
}

export const COURSES: Course[] = WORLDS.map((w) => ({ n: w.n, name: w.name, place: w.place }));

/** Which course a hole is played on: 1..9 in order, 18 holes go around twice, playoffs at Machu Picchu (world 9). */
export function courseOf(hole: number, totalHoles: number): Course {
  const n = hole > totalHoles ? 9 : ((Math.max(1, hole) - 1) % 9) + 1;
  return COURSES[n - 1]!;
}

/** A card's spoken name ("Sphinx Trap (10)"), or the number for the plain cards. */
export function cardLabel(value: number): string {
  const name = CARD_NAMES[value] ?? '';
  const n = value === -5 ? 'minus five' : String(value);
  return name ? `${name.replace(/!$/, '')} (${n})` : n;
}

// ---------------------------------------------------------------------------
// Names are data

/** Strip anything that could read as an audio cue, markup or an instruction before a name nears a prompt. */
export function cleanReadoutName(name: unknown): string {
  const s = String(name ?? '')
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e]/g, '')
    .replace(/[[\]{}<>|`*_#\\]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NAME)
    .trim();
  return s || 'Player';
}

/** Free text from the model or a client (a running gag): no cues, no markup, capped. */
export function cleanPhrase(text: unknown, max = 240): string {
  return String(text ?? '')
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e]/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[`*_#\\|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max)
    .trim();
}

// ---------------------------------------------------------------------------
// What a player's cards did on the hole (the analog of Single Didge's hand notes)

export type NoteKind = 'eurekaPair' | 'bonus' | 'pair' | 'caught' | 'patience';
export const NOTE_KINDS: readonly NoteKind[] = ['eurekaPair', 'bonus', 'pair', 'caught', 'patience'];

/** Structured, so the server writes the words: a client can only send kinds and small numbers. */
export interface HoleNoteInput {
  kind: NoteKind;
  /** eurekaPair: columns of matched −5s. patience: skips taken. */
  count?: number;
  /** bonus: matched columns in the group (2..4). */
  k?: number;
  /** bonus / pair: the matched card value. */
  value?: number;
  /** caught: values of 10+ that stayed face down until the end and counted. */
  values?: number[];
}

const isCard = (v: unknown): v is number => Number.isInteger(v) && (v === -5 || ((v as number) >= 0 && (v as number) <= 12));

export function cleanNotes(raw: unknown): HoleNoteInput[] {
  if (!Array.isArray(raw)) return [];
  const out: HoleNoteInput[] = [];
  for (const n of raw.slice(0, 8)) {
    if (!n || typeof n !== 'object') continue;
    const o = n as Record<string, unknown>;
    const kind = o.kind as NoteKind;
    if (!NOTE_KINDS.includes(kind)) continue;
    if (kind === 'eurekaPair' && Number.isInteger(o.count) && (o.count as number) >= 1 && (o.count as number) <= 4) out.push({ kind, count: o.count as number });
    else if (kind === 'patience' && Number.isInteger(o.count) && (o.count as number) >= PATIENCE_SKIPS && (o.count as number) <= 200) out.push({ kind, count: o.count as number });
    else if (kind === 'bonus' && Number.isInteger(o.k) && (o.k as number) >= 2 && (o.k as number) <= 4 && isCard(o.value)) out.push({ kind, k: o.k as number, value: o.value as number });
    else if (kind === 'pair' && isCard(o.value) && (o.value as number) >= 10) out.push({ kind, value: o.value as number });
    else if (kind === 'caught' && Array.isArray(o.values)) {
      const values = o.values.filter((v) => isCard(v) && v >= 10).slice(0, 8) as number[];
      if (values.length) out.push({ kind, values });
    }
  }
  return out;
}

/** A note as a past-tense spoken fact. Only this text, never the client's, reaches the model. */
export function describeNote(n: HoleNoteInput): string {
  switch (n.kind) {
    case 'eurekaPair':
      return n.count === 1
        ? 'matched a pair of Eurekas (minus fives) in one column, which counts minus ten instead of canceling'
        : `matched ${n.count} columns of Eurekas (minus fives), minus ten each`;
    case 'bonus': {
      const bonus = n.k === 2 ? 10 : n.k === 3 ? 15 : 20;
      return `matched ${n.k} columns of the same card, ${cardLabel(n.value!)}, for the minus ${bonus} matched-column bonus`;
    }
    case 'pair':
      return `matched a column of two ${cardLabel(n.value!)}, which canceled to zero`;
    case 'caught': {
      const vs = n.values!;
      return vs.length === 1
        ? `was caught holding a ${cardLabel(vs[0]!)} face down until the end; it counted`
        : `was caught holding ${vs.length} big cards face down until the end (${vs.map(cardLabel).join(', ')}); they counted`;
    }
    case 'patience':
      return `skipped ${n.count} times, keeping a card face down and waiting`;
  }
}

/** Notes for one revealed grid. `caught` comes from the hole's 'revealed' event. */
export function notesForGrid(state: GameState, seat: number, revealedValues: number[]): HoleNoteInput[] {
  const p = state.players[seat];
  if (!p || p.grid.length !== 8 || p.grid.some((s) => !s.card)) return [];
  const sb = scoreGrid(p.grid, { revealAll: true, options: state.options });
  const out: HoleNoteInput[] = [];
  const eurekas = sb.columns.filter((c) => c.kind === 'minusFivePair').length;
  if (eurekas) out.push({ kind: 'eurekaPair', count: eurekas });
  for (const b of sb.bonuses) out.push({ kind: 'bonus', k: b.k, value: b.value });
  const inBonus = new Set(sb.bonuses.flatMap((b) => b.columns));
  for (const c of sb.columns) if (c.kind === 'pair' && (c.top ?? 0) >= 10 && !inBonus.has(c.column)) out.push({ kind: 'pair', value: c.top! });
  const caught = revealedValues.filter((v) => v >= 10);
  if (caught.length) out.push({ kind: 'caught', values: caught });
  if (p.skipsThisHole >= PATIENCE_SKIPS) out.push({ kind: 'patience', count: p.skipsThisHole });
  return out;
}

// ---------------------------------------------------------------------------
// The request (what a client or the room sends for one readout)

export interface ReadoutPlayerInput {
  name: string;
  isBot?: boolean;
  /** The member's privilege (TIBBS-VOICE.md 7.22): the human in a vs-computer game. Never online. */
  isFirst?: boolean;
  /** This hole's score; null for a player sitting out a playoff hole. */
  holeScore: number | null;
  /** Regulation total before / after this hole (equal on a playoff hole). */
  prevTotal: number;
  total: number;
  wentOut: boolean;
  /** Regulation hole scores so far, in order. */
  scores: number[];
  notes?: HoleNoteInput[];
}

export interface ReadoutRequest {
  gameId: string;
  hole: number;
  totalHoles: number;
  gameOver: boolean;
  players: ReadoutPlayerInput[];
  /** Who went out on each regulation hole so far (seat index), null when nobody did or it is not known. */
  outs?: (number | null)[];
  reshuffles?: number;
  /** A sudden-death hole follows this one: 'start' after regulation, 'again' after a tied playoff hole. */
  playoffNext?: 'start' | 'again' | null;
  /** gameOver: the winners' seats (more than one only with a shared win). */
  winners?: number[];
  previousScripts?: string[];
  gags?: Record<string, string>;
  usedMotifs?: string[];
}

class BadRequest extends Error {}

const int = (v: unknown): v is number => Number.isInteger(v);

/** Validate and normalize a request from the network. Throws an Error with a short message. */
export function validateReadoutRequest(body: unknown): ReadoutRequest {
  if (!body || typeof body !== 'object') throw new BadRequest('Bad request.');
  const b = body as Record<string, unknown>;
  const gameId = String(b.gameId ?? '');
  if (!/^[A-Za-z0-9:_-]{4,64}$/.test(gameId)) throw new BadRequest('Bad game id.');
  const totalHoles = Number(b.totalHoles);
  if (!int(totalHoles) || totalHoles < 1 || totalHoles > MAX_TOTAL_HOLES) throw new BadRequest('Bad hole count.');
  const hole = Number(b.hole);
  if (!int(hole) || hole < 1 || hole > totalHoles + MAX_PLAYOFF_HOLES) throw new BadRequest('Bad hole.');
  const playoff = hole > totalHoles;
  const gameOver = b.gameOver === true;
  if (!Array.isArray(b.players) || b.players.length < 2 || b.players.length > MAX_READOUT_PLAYERS) throw new BadRequest('Bad player list.');
  const regPlayed = Math.min(hole, totalHoles);
  let outs = 0;
  let firsts = 0;
  const players: ReadoutPlayerInput[] = b.players.map((raw) => {
    if (!raw || typeof raw !== 'object') throw new BadRequest('Bad player.');
    const p = raw as Record<string, unknown>;
    const holeScore = p.holeScore === null ? null : Number(p.holeScore);
    const total = Number(p.total);
    const prevTotal = Number(p.prevTotal);
    if (holeScore !== null && (!int(holeScore) || holeScore < MIN_HOLE_SCORE || holeScore > MAX_HOLE_SCORE)) throw new BadRequest('Bad score.');
    if (!int(total) || !int(prevTotal)) throw new BadRequest('Bad score.');
    if (!Array.isArray(p.scores) || p.scores.length !== regPlayed) throw new BadRequest('Bad history.');
    const scores = p.scores.map(Number);
    if (scores.some((v) => !int(v) || v < MIN_HOLE_SCORE || v > MAX_HOLE_SCORE)) throw new BadRequest('Bad history.');
    if (scores.reduce((a, v) => a + v, 0) !== total) throw new BadRequest('Bad history.');
    if (playoff) {
      if (prevTotal !== total) throw new BadRequest('Bad score.');
    } else {
      if (holeScore === null || scores[hole - 1] !== holeScore || prevTotal + holeScore !== total) throw new BadRequest('Bad score.');
    }
    const wentOut = p.wentOut === true;
    if (wentOut) {
      outs++;
      if (holeScore === null) throw new BadRequest('Bad score.');
    }
    const isFirst = p.isFirst === true;
    if (isFirst) firsts++;
    return {
      name: cleanReadoutName(p.name),
      isBot: p.isBot === true,
      isFirst,
      holeScore,
      prevTotal,
      total,
      wentOut,
      scores,
      notes: holeScore === null ? [] : cleanNotes(p.notes),
    };
  });
  if (outs > 1) throw new BadRequest('Only one player goes out.');
  if (firsts > 1) throw new BadRequest('Only one member.');
  if (players.filter((p) => p.holeScore !== null).length < 2) throw new BadRequest('Bad score.');
  let roundOuts: (number | null)[] | undefined;
  if (Array.isArray(b.outs)) {
    if (b.outs.length > regPlayed) throw new BadRequest('Bad history.');
    roundOuts = b.outs.map((o) => (o === null || o === undefined ? null : Number(o)));
    if (roundOuts.some((o) => o !== null && (!int(o) || o < 0 || o >= players.length))) throw new BadRequest('Bad history.');
  }
  const winners = Array.isArray(b.winners) ? b.winners.map(Number).filter((w) => int(w) && w >= 0 && w < players.length) : undefined;
  const reshuffles = int(b.reshuffles) && (b.reshuffles as number) >= 0 && (b.reshuffles as number) < 100 ? (b.reshuffles as number) : 0;
  const playoffNext = b.playoffNext === 'start' || b.playoffNext === 'again' ? b.playoffNext : null;
  const names = new Set(players.map((p) => p.name));
  return {
    gameId,
    hole,
    totalHoles,
    gameOver,
    players,
    ...(roundOuts ? { outs: roundOuts } : {}),
    reshuffles,
    playoffNext: gameOver ? null : playoffNext,
    ...(gameOver && winners?.length ? { winners } : {}),
    previousScripts: cleanPreviousScripts(b.previousScripts),
    gags: cleanGags(b.gags, names),
    usedMotifs: Array.isArray(b.usedMotifs) ? b.usedMotifs.map(String).slice(-96) : [],
  };
}

export function cleanPreviousScripts(scripts: unknown): string[] {
  if (!Array.isArray(scripts)) return [];
  return scripts
    .slice(-MAX_PREVIOUS_SCRIPTS)
    .map((t) => cleanPhrase(t, MAX_PREVIOUS_CHARS))
    .filter((t) => t.length > 0);
}

export function cleanGags(gags: unknown, validNames: Set<string> | null = null): Record<string, string> {
  if (!gags || typeof gags !== 'object' || Array.isArray(gags)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(gags as Record<string, unknown>)) {
    if (!String(k).trim()) continue;
    const name = cleanReadoutName(k);
    if (validNames && !validNames.has(name)) continue;
    const text = cleanPhrase(v, MAX_GAG_CHARS);
    if (!text) continue;
    out[name] = text;
    if (Object.keys(out).length >= MAX_READOUT_PLAYERS) break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Building the request from a game state (the client vs computer; the room online)

/** Events since the current hole started (the log is capped). */
function holeEvents(state: GameState): GameEvent[] {
  const out: GameEvent[] = [];
  for (let i = state.log.length - 1; i >= 0; i--) {
    const e = state.log[i]!;
    if (e.type === 'holeStarted') break;
    out.push(e);
  }
  return out.reverse();
}

/** Who went out on the hole just finished (null: nobody, or the hole ended on the guard). */
export function wentOutThisHole(state: GameState): number | null {
  const ev = holeEvents(state).find((e) => e.type === 'wentOut');
  if (ev && ev.type === 'wentOut') return ev.player;
  return state.wentOutBy;
}

/** Record this hole's goer-out in an outs list (index = regulation hole - 1). Returns a new list. */
export function recordOut(outs: readonly (number | null)[], state: GameState): (number | null)[] {
  if (state.hole > state.totalHoles) return [...outs];
  const next = [...outs];
  while (next.length < state.hole) next.push(null);
  next[state.hole - 1] = wentOutThisHole(state);
  return next.slice(0, state.totalHoles);
}

export interface RequestContext {
  gameId: string;
  /** Display name per seat (the room's seat names, or the saved player name). */
  names: string[];
  /** Seat that gets the member's privilege (vs computer only); null online. */
  firstSeat: number | null;
  outs?: readonly (number | null)[];
  previousScripts?: string[];
  gags?: Record<string, string>;
  usedMotifs?: string[];
}

/**
 * The request for the hole just finished. `state.phase` must be 'holeOver' or
 * 'gameOver' (all cards are face up then, even in a redacted view).
 */
export function readoutRequestFromState(state: GameState, ctx: RequestContext): ReadoutRequest {
  if (state.phase !== 'holeOver' && state.phase !== 'gameOver') throw new Error('The hole is not over yet.');
  const hole = state.hole;
  const playoff = hole > state.totalHoles;
  const out = wentOutThisHole(state);
  const revealedEv = [...holeEvents(state)].reverse().find((e) => e.type === 'revealed');
  const revealed = revealedEv && revealedEv.type === 'revealed' ? revealedEv.cards : [];
  const players: ReadoutPlayerInput[] = state.players.map((p, seat) => {
    const total = p.holeScores.reduce((a, v) => a + v, 0);
    const holeScore = playoff ? (p.playoffScores[p.playoffScores.length - 1] ?? null) : (p.holeScores[p.holeScores.length - 1] ?? 0);
    const prevTotal = playoff ? total : total - (holeScore ?? 0);
    const caughtValues = revealed
      .filter((r) => r.player === seat && r.card.value >= 10)
      .filter((r) => {
        const partner = p.grid[r.slot < 4 ? r.slot + 4 : r.slot - 4]?.card?.value;
        return partner !== r.card.value; // a matched big pair canceled; it was not "caught"
      })
      .map((r) => r.card.value);
    return {
      name: cleanReadoutName(ctx.names[seat] ?? p.name),
      isBot: p.isBot,
      isFirst: ctx.firstSeat === seat,
      holeScore,
      prevTotal,
      total,
      wentOut: out === seat && holeScore !== null,
      scores: p.holeScores.slice(),
      notes: holeScore === null ? [] : notesForGrid(state, seat, caughtValues),
    };
  });
  const contenders = state.playoffPlayers;
  const gameOver = state.phase === 'gameOver';
  return {
    gameId: ctx.gameId,
    hole,
    totalHoles: state.totalHoles,
    gameOver,
    players,
    outs: (ctx.outs ? recordOut(ctx.outs, state) : recordOut([], state)).slice(0, Math.min(hole, state.totalHoles)),
    reshuffles: state.reshuffles,
    playoffNext: !gameOver && contenders && contenders.length > 1 ? (playoff ? 'again' : 'start') : null,
    ...(gameOver && state.winners ? { winners: state.winners.slice() } : {}),
    previousScripts: ctx.previousScripts ?? [],
    gags: ctx.gags ?? {},
    usedMotifs: ctx.usedMotifs ?? [],
  };
}

// ---------------------------------------------------------------------------
// Gaps and milestones

export type GapKind = 'tied' | 'putt' | 'whisker' | 'iron' | 'far' | 'behind';

/** The category a gap to the player just ahead falls in; the model honors it and riffs on the wording. */
export function gapKind(gap: number): GapKind {
  if (gap === 0) return 'tied';
  if (gap <= 2) return 'putt';
  if (gap <= 5) return 'whisker';
  if (gap <= 9) return 'iron';
  if (gap >= 30) return 'far';
  return 'behind';
}

const points = (n: number) => `${n} ${n === 1 ? 'point' : 'points'}`;

/** Plain words a casual player understands: points, not strokes; no golf jargon. */
export function gapPhrase(gap: number, ahead: string): string {
  switch (gapKind(gap)) {
    case 'tied':
      return `level with ${ahead}`;
    case 'putt':
      return `right on ${ahead}'s heels, ${points(gap)} back`;
    case 'whisker':
      return `close behind ${ahead}, ${points(gap)} back`;
    case 'iron':
      return `well behind ${ahead}, ${points(gap)} back`;
    case 'far':
      return `nowhere near ${ahead}, ${points(gap)} back`;
    default:
      return `${points(gap)} behind ${ahead}`;
  }
}

export function finalGapPhrase(gap: number, ahead: string): string {
  return gap === 0 ? `finished level with ${ahead}` : `finished ${points(gap)} behind ${ahead}`;
}

/** Milestones on the running total, including ones crossed this hole (present tense). */
export function milestonesFor(prevTotal: number, total: number): string[] {
  const m: string[] = [];
  if (prevTotal >= 0 && total < 0) m.push('has gone below zero overall');
  if (prevTotal < 100 && total >= 100) m.push('has passed one hundred points');
  if (prevTotal < 200 && total >= 200) m.push('has passed two hundred points');
  return m;
}

/** The ceremony's milestones: achievements of the final total, in past tense. */
export function finalMilestonesFor(total: number): string[] {
  const m: string[] = [];
  if (total < 0) m.push('finished the whole tour below zero');
  if (total >= 200) m.push('finished past two hundred points');
  else if (total >= 100) m.push('finished past one hundred points');
  return m;
}

// ---------------------------------------------------------------------------
// The facts

export type Slot = 'gold' | 'silver' | 'bronze' | 'last' | 'middle';

export interface HistoryHole {
  hole: number;
  course: string;
  scores: Record<string, number>;
  totals: Record<string, number>;
  wentOut: string | null;
  leaders: string[];
}

export interface Standing {
  name: string;
  isBot: boolean;
  isFirst: boolean;
  seat: number;
  place: number;
  position: number;
  slot: Slot;
  isLast: boolean;
  total: number;
  prevTotal?: number;
  holeScore?: number | null;
  wentOut?: boolean;
  sittingOut?: boolean;
  gapBehind: number | null;
  gapKind: GapKind | null;
  gapPhrase: string | null;
  aheadName: string | null;
  gapToLeader: number;
  milestones: string[];
  /** Per-hole flags (absent in the ceremony). */
  lowOnHole?: boolean;
  highOnHole?: boolean;
  legendary?: boolean;
  zero?: boolean;
  underZero?: boolean;
  hm?: boolean;
  ohDear?: boolean;
  outcome?: 'outAndWon' | 'rushed' | null;
  comeback?: boolean;
  collapse?: boolean;
  newLeader?: boolean;
  notes?: string[];
  prevPosition?: number;
  moved?: number;
  /** Game so far. */
  outs?: number;
  ledHoles?: number;
  bestHole?: { hole: number; score: number; course: string } | null;
  worstHole?: { hole: number; score: number; course: string } | null;
  prizeLabel?: string;
}

export interface Medalist {
  medal: 'gold' | 'silver' | 'bronze';
  names: string[];
  total: number;
  tied: boolean;
}

export interface Facts {
  hole: number;
  totalHoles: number;
  playoff: boolean;
  gameOver: boolean;
  isFinal: boolean;
  course: Course;
  lap: 1 | 2;
  holesLeft: number;
  nextCourse: string | null;
  playoffNext: 'start' | 'again' | null;
  playerCount: number;
  firstPlayer: string | null;
  standings: Standing[];
  medals: { gold: string[]; silver: string[]; bronze: string[]; last: string[] };
  holeSummary: {
    low: number;
    lowNames: string[];
    high: number;
    highNames: string[];
    wentOut: string | null;
    outcome: 'outAndWon' | 'rushed' | null;
    reshuffles: number;
    halved: boolean;
  } | null;
  history: {
    holes: HistoryHole[];
    bestHole: { name: string; score: number; hole: number; course: string } | null;
    worstHole: { name: string; score: number; hole: number; course: string } | null;
    leaderStreak: { name: string; holes: number } | null;
    longestLeaderStreak: { name: string; holes: number } | null;
    leadChanges: number;
    formerLeaders: { name: string; ledAfterHole: number; position: number }[];
    comebacks: { name: string; lastAfterHole: number; position: number }[];
    mostOuts: { names: string[]; count: number } | null;
    neverOut: string[];
  };
  medalists?: Medalist[];
  lastPlace?: { names: string[]; total: number; tied: boolean } | null;
  winners?: string[];
  decidedInPlayoff?: boolean;
  gags: Record<string, string>;
  usedMotifs: string[];
  previousScripts: string[];
}

/** Compute the facts for one validated request. */
export function computeFacts(input: ReadoutRequest): Facts {
  const { hole, totalHoles } = input;
  const playoff = hole > totalHoles;
  const isFinal = input.gameOver;
  const seats = input.players.map((p, seat) => ({ ...p, seat }));
  // Only one member: the first flagged wins.
  let firstSeen = false;
  for (const s of seats) {
    if (s.isFirst && firstSeen) s.isFirst = false;
    if (s.isFirst) firstSeen = true;
  }
  const n = seats.length;
  // At the end, a playoff (or a shared win) decides who holds the Amphora: the engine's winners rank first.
  const winSet = isFinal && input.winners?.length && input.winners.length < n ? new Set(input.winners) : null;
  const won = (seat: number) => (winSet ? (winSet.has(seat) ? 0 : 1) : 0);
  const ordered = [...seats].sort((a, b) => won(a.seat) - won(b.seat) || a.total - b.total || a.seat - b.seat);
  const lastTotal = ordered[n - 1]!.total;
  const allTied = !winSet && ordered[0]!.total === lastTotal;
  const leaderTotal = ordered[0]!.total;
  const placeOf = (p: { seat: number; total: number }) =>
    winSet ? (winSet.has(p.seat) ? 1 : 1 + winSet.size + ordered.filter((x) => !winSet.has(x.seat) && x.total < p.total).length) : 1 + ordered.filter((x) => x.total < p.total).length;

  const played = seats.filter((p) => p.holeScore !== null);
  const low = Math.min(...played.map((p) => p.holeScore!));
  const high = Math.max(...played.map((p) => p.holeScore!));
  const prevOrdered = [...seats].sort((a, b) => a.prevTotal - b.prevTotal || a.seat - b.seat);
  const prevBest = prevOrdered[0]!.prevTotal;
  const prevWorst = prevOrdered[n - 1]!.prevTotal;
  const prevTied = prevBest === prevWorst;
  const leadersBefore = seats.filter((p) => p.prevTotal === prevBest).map((p) => p.seat);
  const out = seats.find((p) => p.wentOut) ?? null;
  const outcome = out ? (out.holeScore === low ? 'outAndWon' : 'rushed') : null;

  const standings: Standing[] = ordered.map((p, i) => {
    const place = placeOf(p);
    const ahead = i > 0 ? ordered[i - 1]! : null;
    const gap = ahead ? p.total - ahead.total : 0;
    const isLast = !allTied && p.total === lastTotal && !winSet?.has(p.seat);
    const slot: Slot = place === 1 ? 'gold' : isLast ? 'last' : place === 2 ? 'silver' : place === 3 && n >= 4 ? 'bronze' : 'middle';
    const st: Standing = {
      name: p.name,
      isBot: Boolean(p.isBot),
      isFirst: Boolean(p.isFirst),
      seat: p.seat,
      place,
      position: i + 1,
      slot,
      isLast,
      total: p.total,
      gapBehind: ahead ? gap : null,
      gapKind: ahead ? (isFinal ? (gap === 0 ? 'tied' : 'behind') : gapKind(gap)) : null,
      gapPhrase: ahead ? (isFinal ? finalGapPhrase(gap, ahead.name) : gapPhrase(gap, ahead.name)) : null,
      aheadName: ahead ? ahead.name : null,
      gapToLeader: p.total - leaderTotal,
      milestones: isFinal ? finalMilestonesFor(p.total) : milestonesFor(p.prevTotal, p.total),
    };
    if (p.holeScore === null) {
      st.sittingOut = true;
      st.holeScore = null;
      return st;
    }
    const prevPosition = prevOrdered.findIndex((x) => x.seat === p.seat) + 1;
    const hs = p.holeScore;
    st.prevTotal = p.prevTotal;
    st.holeScore = hs;
    st.wentOut = p.wentOut;
    st.lowOnHole = hs === low;
    st.highOnHole = hs === high && high !== low;
    st.legendary = hs <= LEGENDARY_SCORE;
    st.zero = hs === 0;
    st.underZero = hs < 0;
    st.hm = hs >= HM_SCORE && hs < OH_DEAR_SCORE;
    st.ohDear = hs >= OH_DEAR_SCORE;
    st.outcome = p.wentOut ? outcome : null;
    st.prevPosition = prevPosition;
    st.moved = prevPosition - (i + 1);
    const losingBefore = !prevTied && (p.prevTotal === prevWorst || prevPosition > Math.ceil(n / 2));
    st.comeback = !playoff && losingBefore && (st.lowOnHole || st.moved >= 2);
    st.collapse = !playoff && !prevTied && p.prevTotal === prevBest && hs >= HM_SCORE;
    st.newLeader = !playoff && place === 1 && !leadersBefore.includes(p.seat);
    const notes = (p.notes ?? []).map(describeNote);
    if (notes.length) st.notes = notes;
    return st;
  });

  const names = (pred: (s: Standing) => boolean) => standings.filter(pred).map((s) => s.name);
  const course = courseOf(hole, totalHoles);
  const history = computeHistory(seats, standings, input.outs ?? [], totalHoles);
  const firstPlayer = standings.find((s) => s.isFirst)?.name ?? null;
  const facts: Facts = {
    hole,
    totalHoles,
    playoff,
    gameOver: input.gameOver,
    isFinal,
    course,
    lap: playoff || hole > 9 ? 2 : 1,
    holesLeft: playoff ? 0 : Math.max(0, totalHoles - hole),
    nextCourse: isFinal ? null : input.playoffNext ? COURSES[8]!.name : courseOf(hole + 1, totalHoles).name,
    playoffNext: input.playoffNext ?? null,
    playerCount: n,
    firstPlayer,
    standings,
    medals: { gold: names((s) => s.slot === 'gold'), silver: names((s) => s.slot === 'silver'), bronze: names((s) => s.slot === 'bronze'), last: names((s) => s.slot === 'last') },
    holeSummary: {
      low,
      lowNames: played.filter((p) => p.holeScore === low).map((p) => p.name),
      high,
      highNames: high === low ? [] : played.filter((p) => p.holeScore === high).map((p) => p.name),
      wentOut: out ? out.name : null,
      outcome,
      reshuffles: input.reshuffles ?? 0,
      halved: played.filter((p) => p.holeScore === low).length > 1,
    },
    history,
    gags: cleanGags(input.gags, new Set(standings.map((s) => s.name))),
    usedMotifs: input.usedMotifs ?? [],
    previousScripts: cleanPreviousScripts(input.previousScripts),
  };
  if (isFinal) return ceremonyFacts(facts, input);
  return facts;
}

/** The game so far: each hole's scores and totals, who went out, who led, and the patterns. */
export function computeHistory(
  seats: (ReadoutPlayerInput & { seat: number })[],
  standings: Standing[],
  outs: readonly (number | null)[],
  totalHoles: number,
): Facts['history'] {
  const holesPlayed = seats[0]!.scores.length;
  const n = seats.length;
  const totals = seats.map(() => 0);
  const holes: HistoryHole[] = [];
  const ledHoles = seats.map(() => 0);
  const ledAfter: number[][] = seats.map(() => []);
  const lastAfter: (number | null)[] = seats.map(() => null);
  const outCount = seats.map(() => 0);
  let leaders: number[] | null = null;
  let leadChanges = 0;
  let streakName: string | null = null;
  let streak = 0;
  let longest: { name: string; holes: number } | null = null;
  for (let h = 1; h <= holesPlayed; h++) {
    seats.forEach((p, i) => (totals[i]! += p.scores[h - 1]!));
    const best = Math.min(...totals);
    const worst = Math.max(...totals);
    const lead = seats.map((_, i) => i).filter((i) => totals[i] === best);
    const o = outs[h - 1];
    if (o !== null && o !== undefined && o < n) outCount[o]!++;
    if (best !== worst) {
      for (const i of lead) {
        ledHoles[i]!++;
        ledAfter[i]!.push(h);
      }
      seats.forEach((_, i) => {
        if (totals[i] === worst) lastAfter[i] = h;
      });
      if (leaders && (leaders.length !== lead.length || !lead.every((i) => leaders!.includes(i)))) leadChanges++;
      const sole = lead.length === 1 ? seats[lead[0]!]!.name : null;
      if (sole && sole === streakName) streak++;
      else {
        streakName = sole;
        streak = sole ? 1 : 0;
      }
      if (streakName && (!longest || streak > longest.holes)) longest = { name: streakName, holes: streak };
    } else {
      streakName = null;
      streak = 0;
    }
    leaders = lead;
    holes.push({
      hole: h,
      course: courseOf(h, totalHoles).name,
      scores: Object.fromEntries(seats.map((p) => [p.name, p.scores[h - 1]!])),
      totals: Object.fromEntries(seats.map((p, i) => [p.name, totals[i]!])),
      wentOut: o !== null && o !== undefined && o < n ? seats[o]!.name : null,
      leaders: best !== worst ? lead.map((i) => seats[i]!.name) : [],
    });
  }
  let best: Facts['history']['bestHole'] = null;
  let worst: Facts['history']['worstHole'] = null;
  seats.forEach((p) =>
    p.scores.forEach((s, h) => {
      if (!best || s < best.score) best = { name: p.name, score: s, hole: h + 1, course: courseOf(h + 1, totalHoles).name };
      if (!worst || s > worst.score) worst = { name: p.name, score: s, hole: h + 1, course: courseOf(h + 1, totalHoles).name };
    }),
  );
  const byName = new Map(standings.map((s) => [s.name, s]));
  const current = standings.filter((s) => s.place === 1).map((s) => s.name);
  const formerLeaders = seats
    .map((p, i) => ({ p, i }))
    .filter(({ p, i }) => ledAfter[i]!.some((h) => h < holesPlayed) && !current.includes(p.name))
    .map(({ p, i }) => ({ name: p.name, ledAfterHole: ledAfter[i]!.filter((h) => h < holesPlayed).at(-1)!, position: byName.get(p.name)?.position ?? 0 }));
  const comebacks = seats
    .map((p, i) => ({ p, i }))
    .filter(({ p, i }) => {
      const st = byName.get(p.name);
      return lastAfter[i] !== null && lastAfter[i]! < holesPlayed && st && st.position <= Math.ceil(n / 2) && !st.isLast;
    })
    .map(({ p, i }) => ({ name: p.name, lastAfterHole: lastAfter[i]!, position: byName.get(p.name)!.position }));
  const maxOuts = Math.max(...outCount);
  for (const st of standings) {
    const i = seats.findIndex((p) => p.seat === st.seat);
    const sc = seats[i]!.scores;
    st.outs = outCount[i]!;
    st.ledHoles = ledHoles[i]!;
    let b: { hole: number; score: number; course: string } | null = null;
    let w: { hole: number; score: number; course: string } | null = null;
    sc.forEach((s, h) => {
      if (!b || s < b.score) b = { hole: h + 1, score: s, course: courseOf(h + 1, totalHoles).name };
      if (!w || s > w.score) w = { hole: h + 1, score: s, course: courseOf(h + 1, totalHoles).name };
    });
    st.bestHole = b;
    st.worstHole = w;
  }
  const knownOuts = outs.filter((o) => o !== null && o !== undefined).length;
  return {
    holes,
    bestHole: best,
    worstHole: worst,
    leaderStreak: streakName ? { name: streakName, holes: streak } : null,
    longestLeaderStreak: longest,
    leadChanges,
    formerLeaders,
    comebacks,
    mostOuts: maxOuts >= 2 ? { names: seats.filter((_, i) => outCount[i] === maxOuts).map((p) => p.name), count: maxOuts } : null,
    neverOut: holesPlayed >= 3 && knownOuts >= 3 ? seats.filter((_, i) => outCount[i] === 0).map((p) => p.name) : [],
  };
}

/**
 * The presentation of the Golden Amphora names only the medalists (the
 * Golden Amphora, the Silver Laurel with 3+ players, the Bronze with 4+) and
 * the Pompeii Prize (last place; everyone tied for last; nobody when all
 * tied). Everyone in between is dropped so the model cannot mention them, and
 * the final hole itself is not recapped (Single Didge's ceremony rule).
 */
export function ceremonyFacts(facts: Facts, input: ReadoutRequest): Facts {
  const keep = facts.standings.filter((s) => s.slot !== 'middle');
  const kept = new Set(keep.map((s) => s.name));
  for (const st of keep) {
    if (st.aheadName && !kept.has(st.aheadName)) {
      st.gapPhrase = null;
      st.gapKind = null;
      st.aheadName = null;
      st.gapBehind = null;
    }
    for (const f of ['prevTotal', 'holeScore', 'wentOut', 'sittingOut', 'lowOnHole', 'highOnHole', 'legendary', 'zero', 'underZero', 'hm', 'ohDear', 'outcome', 'comeback', 'collapse', 'newLeader', 'notes', 'prevPosition', 'moved'] as const) {
      delete st[f];
    }
    if (st.slot === 'last') st.prizeLabel = LAST_PRIZE.name;
  }
  const named = (slot: Slot) => keep.filter((s) => s.slot === slot).map((s) => s.name);
  const totalOf = (slot: Slot) => keep.find((s) => s.slot === slot)?.total ?? 0;
  const medalists: Medalist[] = (['gold', 'silver', 'bronze'] as const)
    .map((medal) => ({ medal, names: named(medal), total: totalOf(medal), tied: named(medal).length > 1 }))
    .filter((m) => m.names.length > 0);
  const last = named('last');
  const pick = <T>(o: Record<string, T>) => Object.fromEntries(Object.entries(o).filter(([k]) => kept.has(k)));
  const h = facts.history;
  const finalReg = Math.min(facts.hole, facts.totalHoles);
  const history: Facts['history'] = {
    holes: h.holes.filter((x) => x.hole < finalReg).map((x) => ({ ...x, scores: pick(x.scores), totals: pick(x.totals), wentOut: x.wentOut && kept.has(x.wentOut) ? x.wentOut : null, leaders: x.leaders.filter((l) => kept.has(l)) })),
    bestHole: h.bestHole && kept.has(h.bestHole.name) && h.bestHole.hole < finalReg ? h.bestHole : null,
    worstHole: null,
    leaderStreak: h.leaderStreak && kept.has(h.leaderStreak.name) ? h.leaderStreak : null,
    longestLeaderStreak: h.longestLeaderStreak && kept.has(h.longestLeaderStreak.name) ? h.longestLeaderStreak : null,
    leadChanges: h.leadChanges,
    formerLeaders: h.formerLeaders.filter((x) => kept.has(x.name)),
    comebacks: h.comebacks.filter((x) => kept.has(x.name)),
    mostOuts: h.mostOuts && h.mostOuts.names.some((x) => kept.has(x)) ? { ...h.mostOuts, names: h.mostOuts.names.filter((x) => kept.has(x)) } : null,
    neverOut: h.neverOut.filter((x) => kept.has(x)),
  };
  const winners = (input.winners ?? []).map((w) => cleanReadoutName(input.players[w]?.name)).filter((x) => kept.has(x));
  return {
    ...facts,
    firstPlayer: facts.firstPlayer && kept.has(facts.firstPlayer) ? facts.firstPlayer : null,
    standings: keep,
    medals: { gold: named('gold'), silver: named('silver'), bronze: named('bronze'), last },
    medalists,
    lastPlace: last.length ? { names: last, total: totalOf('last'), tied: last.length > 1 } : null,
    ...(winners.length ? { winners } : {}),
    decidedInPlayoff: facts.playoff,
    holeSummary: null,
    history,
    gags: pick(facts.gags),
  };
}

/** A short, stable fingerprint of the facts: the cache key and the replay check. */
export function factsHash(facts: Facts): string {
  const s = JSON.stringify([
    facts.hole,
    facts.gameOver,
    facts.standings.map((p) => [p.name, p.total, p.holeScore ?? null, p.wentOut ?? false, p.notes ?? []]),
  ]);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** The readout key a hole end is known by on every device: 'h<hole>' or 'final'. */
export function readoutKeyFor(state: Pick<GameState, 'phase' | 'hole'>): string | null {
  if (state.phase === 'gameOver') return 'final';
  if (state.phase === 'holeOver') return `h${state.hole}`;
  return null;
}

